/**
 * ANATOMY · Offers page (/pages/offers) — dynamic first-order code.
 *
 * Flow:
 *  1) Reveal (Turnstile-gated) → POST /api/offer-code → unique single-use code, 48h.
 *  2) Show code + auto-apply link + 48h countdown. Cached in localStorage so a reload
 *     returns the same code with no re-solve.
 *  3) Save with email → POST /api/offer-save → extend code to ~15 days + Klaviyo enroll.
 *  4) Graceful fallback: if minting is unavailable (worker not yet wired), switch to
 *     email-only mode → POST /api/lead-capture → welcome email delivers the code.
 *
 * No secrets here. Turnstile SITE key + worker URL come from the section config.
 */
(function () {
  'use strict';

  var CACHE_KEY = 'anatomy_offer_v1';
  var VID_KEY = 'anatomy_offers_vid';

  function uuid() {
    try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) {}
    return 'ao-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  function visitorId() {
    // Prefer an existing ANATOMY/PostHog visitor id so the code + save link the same
    // person the rest of the site already knows; otherwise mint a stable local one.
    try {
      if (window.posthog && typeof window.posthog.get_distinct_id === 'function') {
        var d = window.posthog.get_distinct_id();
        if (d) return d;
      }
    } catch (e) {}
    try {
      var v = localStorage.getItem(VID_KEY);
      if (!v) { v = uuid(); localStorage.setItem(VID_KEY, v); }
      return v;
    } catch (e) { return uuid(); }
  }

  function readCache() {
    try { return JSON.parse(localStorage.getItem(CACHE_KEY) || 'null'); } catch (e) { return null; }
  }
  function writeCache(obj) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(obj)); } catch (e) {}
  }

  function validEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim()); }

  function track(name, props) {
    try { if (window.posthog && window.posthog.capture) window.posthog.capture(name, props || {}); } catch (e) {}
  }

  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function OffersSection(root, cfg) {
    this.root = root;
    this.cfg = cfg;
    this.token = null;           // Turnstile token
    this.widgetId = null;
    this.countdownTimer = null;
    this.el = {
      section: root.querySelector('[data-anatomy-offers]') || root,
      gate: root.querySelector('[data-ao-gate]'),
      revealPanel: root.querySelector('[data-ao-reveal-panel]'),
      revealBtn: root.querySelector('[data-ao-reveal]'),
      turnstile: root.querySelector('[data-ao-turnstile]'),
      revealLede: root.querySelector('[data-ao-reveal-lede]'),
      code: root.querySelector('[data-ao-code]'),
      copy: root.querySelector('[data-ao-copy]'),
      apply: root.querySelector('[data-ao-apply]'),
      countdownWrap: root.querySelector('[data-ao-countdown-wrap]'),
      countdown: root.querySelector('[data-ao-countdown]'),
      save: root.querySelector('[data-ao-save]'),
      email: root.querySelector('[data-ao-email]'),
      saveBtn: root.querySelector('[data-ao-save-btn]'),
      msg: root.querySelector('[data-ao-msg]'),
    };
    this.init();
  }

  OffersSection.prototype.init = function () {
    var self = this;
    var el = this.el;
    if (el.revealBtn) el.revealBtn.addEventListener('click', function () { self.onReveal(); });
    if (el.copy) el.copy.addEventListener('click', function () { self.onCopy(); });
    if (el.saveBtn) el.saveBtn.addEventListener('click', function () { self.onSave(); });
    if (el.email) el.email.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); self.onSave(); } });

    // If a valid, unexpired code is already cached, show it immediately.
    var cached = readCache();
    if (cached && cached.code && cached.expires_at && new Date(cached.expires_at).getTime() > Date.now()) {
      this.showCode(cached, true);
    } else if (this.cfg.turnstileSiteKey && this.cfg.instantEnabled !== false) {
      this.renderTurnstile();
    }
  };

  OffersSection.prototype.renderTurnstile = function () {
    var self = this;
    var el = this.el;
    if (!el.turnstile || !this.cfg.turnstileSiteKey) return;
    var tries = 0;
    (function wait() {
      if (window.turnstile && typeof window.turnstile.render === 'function') {
        try {
          self.widgetId = window.turnstile.render(el.turnstile, {
            sitekey: self.cfg.turnstileSiteKey,
            action: 'offer_code',
            appearance: 'interaction-only', // invisible unless a challenge is required
            execution: 'execute',           // runs on the reveal click, not on page load
            callback: function (t) { self.token = t; if (self.pendingReveal) { self.pendingReveal = false; self.doMint(); } },
            'expired-callback': function () { self.token = null; },
            'error-callback': function () { self.token = null; if (self.pendingReveal) { self.pendingReveal = false; self.resetReveal('Verification failed — please try again.'); } },
          });
        } catch (e) { /* leave token null; worker fails open if secret unset */ }
        return;
      }
      if (tries++ < 60) setTimeout(wait, 100);
    })();
  };

  OffersSection.prototype.setMsg = function (text, kind) {
    var m = this.el.msg;
    if (!m) return;
    m.textContent = text || '';
    m.className = 'anatomy-offers__msg' + (kind ? ' is-' + kind : '');
  };

  OffersSection.prototype.onReveal = function () {
    var self = this;
    var el = this.el;
    track('offer_reveal_click', {});

    if (this.cfg.instantEnabled === false) { this.enterFallback(); return; }

    if (el.revealBtn) { el.revealBtn.setAttribute('disabled', 'disabled'); el.revealBtn.textContent = 'One moment…'; }

    // Run Turnstile now (invisible unless a challenge is required) if configured and not
    // yet solved; the callback continues to doMint once a token arrives.
    if (this.cfg.turnstileSiteKey && !this.token && this.widgetId != null && window.turnstile) {
      this.pendingReveal = true;
      window.setTimeout(function () { if (self.pendingReveal) { self.pendingReveal = false; self.doMint(); } }, 8000);
      try { window.turnstile.execute(this.widgetId); } catch (e) { this.pendingReveal = false; this.doMint(); }
      return;
    }
    this.doMint();
  };

  OffersSection.prototype.doMint = function () {
    var self = this;
    var el = this.el;
    var body = { visitor_id: visitorId(), turnstile_token: this.token || '' };
    fetch(this.cfg.workerUrl + '/api/offer-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (r) { return r.json().catch(function () { return {}; }); })
      .then(function (data) {
        if (data && data.success && data.code) {
          writeCache({ code: data.code, discount_url: data.discount_url, expires_at: data.expires_at });
          self.showCode(data, false);
          track('offer_code_shown', { reused: !!data.reused });
          return;
        }
        if (data && (data.fallback === 'email' || data.error === 'mint_unavailable')) {
          self.enterFallback();
          return;
        }
        if (data && data.error === 'rate_limited') {
          self.resetReveal('Too many codes from this connection today. Try again tomorrow, or add your email below and we’ll send one.');
          self.enterFallback();
          return;
        }
        if (data && data.error === 'verification_failed') {
          self.resetReveal('Quick check failed — please try again.');
          if (self.widgetId && window.turnstile) { try { window.turnstile.reset(self.widgetId); } catch (e) {} }
          return;
        }
        self.enterFallback();
      })
      .catch(function () { self.enterFallback(); });
  };

  OffersSection.prototype.resetReveal = function (message) {
    var el = this.el;
    if (el.revealBtn) { el.revealBtn.removeAttribute('disabled'); el.revealBtn.textContent = 'Reveal my welcome code'; }
    if (message) { this.setMsg(message, 'err'); }
  };

  OffersSection.prototype.showCode = function (data, silent) {
    var el = this.el;
    if (el.code) el.code.textContent = data.code;
    if (el.apply && data.discount_url) el.apply.setAttribute('href', data.discount_url);
    this.el.section.classList.add('is-revealed');
    this.startCountdown(data.expires_at);
    if (!silent) track('offer_code_revealed', {});
  };

  OffersSection.prototype.startCountdown = function (expiresAt) {
    var el = this.el;
    if (!el.countdown || !expiresAt) return;
    var end = new Date(expiresAt).getTime();
    var self = this;
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    function tick() {
      var ms = end - Date.now();
      if (ms <= 0) {
        clearInterval(self.countdownTimer);
        if (el.countdownWrap) el.countdownWrap.innerHTML = 'This code has expired — <a href="#" data-ao-again>get a fresh one</a>.';
        var again = el.countdownWrap && el.countdownWrap.querySelector('[data-ao-again]');
        if (again) again.addEventListener('click', function (e) { e.preventDefault(); self.regenerate(); });
        return;
      }
      var totalSec = Math.floor(ms / 1000);
      var h = Math.floor(totalSec / 3600);
      var m = Math.floor((totalSec % 3600) / 60);
      var s = totalSec % 60;
      el.countdown.textContent = pad(h) + ':' + pad(m) + ':' + pad(s);
    }
    tick();
    this.countdownTimer = setInterval(tick, 1000);
  };

  OffersSection.prototype.regenerate = function () {
    try { localStorage.removeItem(CACHE_KEY); } catch (e) {}
    this.el.section.classList.remove('is-revealed');
    if (this.el.countdownWrap) {
      this.el.countdownWrap.innerHTML = 'Reserved for you — expires in <b data-ao-countdown>47:59:59</b>';
      this.el.countdown = this.el.countdownWrap.querySelector('[data-ao-countdown]');
    }
    this.resetReveal('');
    if (this.cfg.turnstileSiteKey && !this.widgetId) this.renderTurnstile();
  };

  // Minting unavailable → email-only: the welcome email delivers the code.
  OffersSection.prototype.enterFallback = function () {
    var el = this.el;
    this.el.section.classList.add('is-revealed');
    if (el.revealLede) el.revealLede.textContent = 'Enter your email and we’ll send your personal welcome code straight to your inbox.';
    // Hide code/apply/countdown; keep the email row as the primary action.
    ['code', 'copy', 'apply', 'countdownWrap'].forEach(function (k) { if (el[k]) el[k].style.display = 'none'; });
    var wrap = el.code && el.code.parentElement;
    if (wrap) wrap.style.display = 'none';
    if (el.saveBtn) el.saveBtn.textContent = 'Email me my code';
    this.fallbackMode = true;
    if (el.email) el.email.focus();
  };

  OffersSection.prototype.onCopy = function () {
    var el = this.el;
    var code = el.code ? el.code.textContent.trim() : '';
    if (!code) return;
    var done = function () { el.copy.textContent = 'Copied'; setTimeout(function () { el.copy.textContent = 'Copy'; }, 1600); };
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) { navigator.clipboard.writeText(code).then(done, function () {}); }
      else { var t = document.createElement('textarea'); t.value = code; document.body.appendChild(t); t.select(); document.execCommand('copy'); document.body.removeChild(t); done(); }
    } catch (e) {}
    track('offer_code_copied', {});
  };

  OffersSection.prototype.onSave = function () {
    var self = this;
    var el = this.el;
    var email = el.email ? el.email.value.trim() : '';
    if (!validEmail(email)) { this.setMsg('Please enter a valid email.', 'err'); return; }
    if (el.saveBtn) { el.saveBtn.setAttribute('disabled', 'disabled'); }
    this.setMsg('Saving…', '');

    var cached = readCache();
    var body = {
      visitor_id: visitorId(),
      email: email,
      code: cached && cached.code ? cached.code : '',
      page_context: 'offers',
      source: 'offers_page',
      capture_type: this.fallbackMode ? 'offers_fallback_email' : 'offers_save_code',
      page_path: location.pathname,
      page_url: location.href,
    };

    // Fallback (no minted code): use the canonical lead-capture path (welcome email delivers code).
    var endpoint = this.fallbackMode ? '/api/lead-capture' : '/api/offer-save';
    if (this.fallbackMode) body.step = 'email';

    fetch(this.cfg.workerUrl + endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (r) { return r.json().catch(function () { return {}; }); })
      .then(function (data) {
        if (el.saveBtn) el.saveBtn.removeAttribute('disabled');
        if (data && data.success) {
          if (self.fallbackMode) {
            self.setMsg('Check your inbox — your personal code is on its way.', 'ok');
          } else {
            self.setMsg('Sent. Your code is saved to your inbox and now valid for 15 days.', 'ok');
            if (data.expires_at) self.startCountdown(data.expires_at);
          }
          if (el.email) el.email.setAttribute('disabled', 'disabled');
          track('offer_code_saved', { fallback: !!self.fallbackMode });
        } else {
          self.setMsg('Something went wrong — please try again.', 'err');
        }
      })
      .catch(function () {
        if (el.saveBtn) el.saveBtn.removeAttribute('disabled');
        self.setMsg('Network error — please try again.', 'err');
      });
  };

  function boot() {
    var configs = document.querySelectorAll('script[data-anatomy-offers-config]');
    configs.forEach(function (scriptEl) {
      var cfg;
      try { cfg = JSON.parse(scriptEl.textContent || '{}'); } catch (e) { cfg = {}; }
      if (!cfg.workerUrl) return;
      var root = scriptEl.closest('.shopify-section') || document;
      var section = root.querySelector('[data-anatomy-offers]');
      if (!section) return;
      new OffersSection(root, cfg);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
